import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { translations } from '../data/translations'
import { motion, AnimatePresence } from 'framer-motion'

const Track = () => {
  const { language } = useLanguage()
  const t = translations[language]
  const [searchParams, setSearchParams] = useSearchParams()
  const [trackingId, setTrackingId] = useState('')
  const [trackingResult, setTrackingResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const trackingResultsRef = useRef(null)
  const hasAutoTrackedRef = useRef(false)

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day}/${month}/${year} ${hours}:${minutes}`
    } catch (e) {
      return dateString
    }
  }

  // Format date for display (without time)
  const formatDateDisplay = (dateString) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    } catch (e) {
      return dateString
    }
  }

  // Track order function
  const trackVoucher = async (voucherNo = null) => {
    const voucher = voucherNo || trackingId.trim()
    
    if (!voucher || voucher === '') {
      setError(t.track.errorVoucher)
      return
    }

    setIsLoading(true)
    setError(null)
    setTrackingResult(null)

    try {
      const response = await fetch(
        `https://api-smart.ainsoftdelivery.com/TrackOrder/${voucher}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch order details')
      }

      const data = await response.json()

      if (data && Object.keys(data).length > 0) {
        // Map API response to our tracking result format
        const mappedResult = {
          id: data.VoucherNo || voucher,
          status: data.Status?.Name || (language === 'en' ? 'Unknown Status' : 'حالة غير معروفة'),
          statusAr: data.Status?.Name || 'حالة غير معروفة',
          deliveryDate: data.DeliveryDate ? formatDateDisplay(data.DeliveryDate) : null,
          currentLocation: language === 'en' ? 'In Transit' : 'قيد النقل',
          estimatedDelivery: data.DeliveryDate ? formatDateDisplay(data.DeliveryDate) : null,
          timeline: data.OrderStatus && data.OrderStatus.length > 0
            ? data.OrderStatus.map((statusItem, index) => ({
                status: statusItem.Status?.Name || (language === 'en' ? 'Unknown Status' : 'حالة غير معروفة'),
                time: statusItem.Date ? formatDate(statusItem.Date) : '',
                completed: true,
                icon: index === 0 ? '📦' : index === data.OrderStatus.length - 1 ? '✓' : '🚚',
                isActive: index === data.OrderStatus.length - 1,
              }))
            : [],
          rawData: data, // Keep raw data for reference
        }
        
        setTrackingResult(mappedResult)
        setError(null)
        
        // Scroll to results after a short delay to ensure DOM is updated
        setTimeout(() => {
          if (trackingResultsRef.current) {
            trackingResultsRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            })
          }
        }, 100)
      } else {
        setError(t.track.errorNotFound)
        setTrackingResult(null)
      }
    } catch (err) {
      setError(t.track.errorFailed)
      setTrackingResult(null)
      console.error('Tracking error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Initialize and check for URL parameters
  useEffect(() => {
    const voucher = searchParams.get('voucher')
    
    // Check if voucher parameter exists in URL and we haven't auto-tracked yet
    if (voucher && voucher !== 'Track' && voucher.length > 0 && !hasAutoTrackedRef.current) {
      hasAutoTrackedRef.current = true
      setTrackingId(voucher)
      // Auto-track if voucher is in URL
      trackVoucher(voucher)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleTrack = (e) => {
    e.preventDefault()
    trackVoucher()
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-primary-500 py-8 md:py-12 lg:py-16 overflow-hidden">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {t.track.title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              {t.track.subtitle}
            </p>

            {/* Tracking Form */}
            <form onSubmit={handleTrack} className="max-w-2xl mx-auto">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={trackingId}
                      onChange={(e) => setTrackingId(e.target.value)}
                      placeholder={t.track.placeholder}
                      className="w-full px-6 py-4 rounded-xl bg-white/90 backdrop-blur-sm border-2 border-white/30 focus:outline-none focus:border-white focus:bg-white text-gray-900 text-lg placeholder-gray-500"
                      disabled={isLoading}
                    />
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <span className="animate-spin inline-block w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full"></span>
                        {t.track.trackingLabel}
                      </>
                    ) : (
                      t.track.trackButton
                    )}
                  </button>
                </div>
                {error && (
                  <div className="mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm">
                    {error}
                  </div>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Tracking Result */}
      <AnimatePresence>
        {trackingResult && (
          <motion.section
            ref={trackingResultsRef}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.6 }}
            className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50"
          >
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Status Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-8 md:p-10 border border-gray-100 mb-8"
              >
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-3 bg-primary-50 text-primary-700 px-6 py-3 rounded-full font-semibold mb-4 border-2 border-primary-200">
                    <span className="text-2xl">📍</span>
                    <span>{trackingResult.status}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {language === 'en' ? 'Voucher No:' : 'رقم القسيمة:'}{' '}
                    <span className="text-primary-600">{trackingResult.id}</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">
                        {language === 'en' ? 'Current Status' : 'الحالة الحالية'}
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        {trackingResult.status}
                      </p>
                    </div>
                    {trackingResult.deliveryDate && (
                      <div className="bg-white rounded-xl p-4 border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">
                          {language === 'en' ? 'Delivery Date' : 'تاريخ التسليم'}
                        </p>
                        <p className="text-lg font-semibold text-primary-600">
                          {trackingResult.deliveryDate}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Timeline */}
              {trackingResult.timeline && trackingResult.timeline.length > 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100"
                >
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-8 text-center">
                    {language === 'en' ? 'Status History' : 'سجل الحالة'}
                  </h3>
                  <div className="relative">
                    {/* Vertical Line */}
                    <div className="absolute left-8 md:left-12 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-500 via-primary-400 to-gray-300"></div>
                    
                    <div className="space-y-8">
                      {trackingResult.timeline.map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + index * 0.1 }}
                          className="relative flex items-start gap-6"
                        >
                          {/* Icon Circle */}
                          <div className={`relative z-10 flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center border-4 ${
                            item.completed
                              ? item.isActive
                                ? 'bg-primary-500 border-primary-600 shadow-lg shadow-primary-500/50'
                                : 'bg-primary-100 border-primary-300'
                              : 'bg-gray-100 border-gray-300'
                          }`}>
                            <span className="text-2xl md:text-3xl">{item.icon}</span>
                            {item.isActive && (
                              <motion.div
                                className="absolute inset-0 rounded-full bg-primary-500"
                                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              />
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 pt-2">
                            <div className={`font-bold text-lg md:text-xl mb-2 ${
                              item.completed ? 'text-gray-900' : 'text-gray-400'
                            }`}>
                              {item.status}
                            </div>
                            {item.time && (
                              <div className="text-gray-600 text-sm md:text-base flex items-center gap-2">
                                <span>🕐</span>
                                <span>{item.time}</span>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100 text-center"
                >
                  <p className="text-gray-600">
                    {language === 'en' ? 'No status history available' : 'لا يوجد سجل حالة متاح'}
                  </p>
                </motion.div>
              )}

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
              >
                <button className="px-8 py-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
                  <span>📱</span>
                  <span>{language === 'en' ? 'Get Updates via SMS' : 'احصل على تحديثات عبر SMS'}</span>
                </button>
                <button className="px-8 py-4 bg-white text-primary-600 border-2 border-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-all flex items-center justify-center gap-2">
                  <span>💬</span>
                  <span>{language === 'en' ? 'Contact Support' : 'اتصل بالدعم'}</span>
                </button>
              </motion.div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Help Section */}
      {!trackingResult && (
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {language === 'en' ? 'Need Help?' : 'تحتاج مساعدة؟'}
              </h2>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                {language === 'en'
                  ? 'If you have any questions about your shipment, contact our support team.'
                  : 'إذا كان لديك أي أسئلة حول شحنتك، اتصل بفريق الدعم لدينا.'}
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: '📞',
                  title: language === 'en' ? 'Phone' : 'الهاتف',
                  desc: ['+971 50 277 2521', '050 277 2521'],
                  link: 'tel:+971502772521',
                  color: 'from-blue-500 to-blue-600',
                },
                {
                  icon: '💬',
                  title: language === 'en' ? 'WhatsApp' : 'واتساب',
                  desc: [language === 'en' ? 'Chat with us' : 'تحدث معنا'],
                  link: 'https://wa.me/971502772521',
                  color: 'from-green-500 to-green-600',
                },
                {
                  icon: '📧',
                  title: language === 'en' ? 'Email' : 'البريد الإلكتروني',
                  desc: ['Info@smartmile.ae'],
                  link: 'mailto:Info@smartmile.ae',
                  color: 'from-purple-500 to-purple-600',
                },
              ].map((item, index) => (
                <motion.a
                  key={index}
                  href={item.link}
                  target={item.link.startsWith('http') ? '_blank' : '_self'}
                  rel={item.link.startsWith('http') ? 'noopener noreferrer' : ''}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-transparent hover:border-primary-300"
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <span className="text-3xl">{item.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  {item.desc.map((d, idx) => (
                    <p key={idx} className="text-gray-600">{d}</p>
                  ))}
                </motion.a>
              ))}
            </div>

            {/* Address */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100 mb-8"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-3xl">📍</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {language === 'en' ? 'Address' : 'العنوان'}
                  </h3>
                  <p className="text-gray-700 mb-1">
                    {language === 'en'
                      ? 'International City - Greek Cluster'
                      : 'المدينة الدولية - مجمع اليونان'}
                  </p>
                  <p className="text-gray-700">
                    {language === 'en'
                      ? 'Building K03, Shop Number 5'
                      : 'مبنى K03، متجر رقم 5'}
                  </p>
                  <a
                    href="https://maps.app.goo.gl/t9mympo5UQYGhjgC7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-semibold"
                  >
                    {language === 'en' ? 'View on Google Maps →' : 'عرض على خرائط Google →'}
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Social Media */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {language === 'en' ? 'Follow Us on Social Media' : 'تابعنا على وسائل التواصل الاجتماعي'}
              </h3>
              <div className="flex justify-center gap-6">
                {/* Instagram */}
                <motion.a
                  href="https://www.instagram.com/smartmile.ae"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-14 h-14 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all"
                  title="Instagram"
                >
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </motion.a>
                
                {/* Facebook */}
                <motion.a
                  href="https://www.facebook.com/smartmile.ae/"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all"
                  title="Facebook"
                >
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </motion.a>
                
                {/* TikTok */}
                <motion.a
                  href="https://www.tiktok.com/@smartmile_ae"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-14 h-14 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all"
                  title="TikTok"
                >
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  )
}

export default Track
